import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import moment from 'moment';
import 'moment/locale/pt-br';
import * as api from '../../../apirest';
import requestApi from '../../../apirest';
import { getLoggedUser } from '../../../utils/userUtils';
import { useAuth } from '../../../context/auth';
import ZplModal from './ZplModal';
import Loading from '../../../shared/components/Loading/Loading';
import './StockDetailPage.css';

const EXCLUDED_KEYS = ['_id', 'TOTAL'];
const SIZE_ORDER = ['PP', 'P', 'M', 'G', 'GG', 'XG', 'XGG', 'XXG',
  '34', '36', '38', '40', '42', '44', '46', '48', '50'];
const LOGS_PER_PAGE = 10;

const MOVEMENT_OPTIONS = [
  { value: 'Ajuste manual', label: 'Ajuste manual' },
  { value: 'Entrada mercadoria', label: 'Entrada mercadoria' },
  { value: 'Devolução', label: 'Devolução' },
  { value: 'Saída', label: 'Saída' },
  { value: 'Contagem de Estoque', label: 'Contagem de Estoque' },
];

const orderedSizes = (groupStock) => {
  if (!groupStock) return [];
  return Object.keys(groupStock)
    .filter((k) => !EXCLUDED_KEYS.includes(k))
    .sort((a, b) => {
      const ai = SIZE_ORDER.indexOf(a);
      const bi = SIZE_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
};

const fmt = (val) =>
  Number(val).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const StockDetailPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const auth = useAuth();
  const userAuth = auth.user;
  const userRef = useRef(null);

  const [variant, setVariant] = useState(null);
  const [groupStock, setGroupStock] = useState(null);
  const [costPrice, setCostPrice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showZpl, setShowZpl] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [movement, setMovement] = useState('Ajuste manual');
  const [observation, setObservation] = useState('');

  // History state
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [logsLoading, setLogsLoading] = useState(false);

  const sizes = useMemo(() => orderedSizes(groupStock), [groupStock]);
  const totalPages = Math.ceil(logsTotal / LOGS_PER_PAGE);

  const getUser = useCallback(async () => {
    if (!userRef.current && userAuth) {
      userRef.current = await getLoggedUser(userAuth.uid);
    }
    return userRef.current;
  }, [userAuth]);

  const loadVariant = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await api.get('/variants/findById', { id });
      const v = res?.data;
      if (!v) return;

      const [stockRes, costRes] = await Promise.all([
        api.get('/variants/findTotalStocksBySize?', { id: v._id }),
        v.product?._id
          ? api.get('/totalCost/findByIdProduct', { idProduct: v.product._id })
          : Promise.resolve(null),
      ]);

      setVariant(v);
      setGroupStock({ ...(stockRes?.data?.[0] ?? {}) });

      const costDoc = costRes?.data?.doc;
      if (Array.isArray(costDoc) && costDoc.length > 0) {
        setCostPrice(costDoc[0].price);
      }
    } catch {
      toast.error('Erro ao carregar variante.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadLogs = useCallback(async (page = 1) => {
    if (!id) return;
    setLogsLoading(true);
    try {
      const skip = (page - 1) * LOGS_PER_PAGE;
      const res = await api.get('/logStocks', { variantId: id, limit: LOGS_PER_PAGE, skip });
      setLogs(res?.data?.logStock ?? []);
      setLogsTotal(res?.data?.total ?? 0);
    } catch {
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, [id]);

  useEffect(() => { loadVariant(); getUser(); }, [loadVariant, getUser]);
  useEffect(() => { if (variant) loadLogs(1); }, [variant, loadLogs]);

  const handlePageChange = (page) => {
    setLogsPage(page);
    loadLogs(page);
  };

  const getStockBySize = async (variantId, size) =>
    api.api(`/stocks?variantId=${variantId}&size=${size}`, {});

  const persistStock = (stock) =>
    requestApi('/stocks', { stock }, stock._id ? 'patch' : 'post');

  const saveSize = async (variantId, size, quantity) => {
    const res = await getStockBySize(variantId, size);
    const { total, stock } = res;
    if (total > 1) throw new Error(`Mais de um estoque para tamanho ${size}.`);
    const stockToSave = total === 1
      ? { ...stock[0], variant: stock[0].variant?.[0] ?? stock[0].variant }
      : { variant: { _id: variantId } };
    stockToSave.size = size;
    stockToSave.quantity = quantity;
    stockToSave.lastChangeUser = await getUser();
    return persistStock(stockToSave);
  };

  const handleChange = (size, value) => {
    const num = Math.max(0, parseInt(value, 10) || 0);
    setGroupStock((prev) => {
      const updated = { ...prev, [size]: num };
      updated.TOTAL = Object.keys(updated)
        .filter((k) => !EXCLUDED_KEYS.includes(k))
        .reduce((s, k) => s + (Number(updated[k]) || 0), 0);
      return updated;
    });
  };

  const persistLog = async (stock) => {
    try {
      await requestApi('/logStocks', {
        logStock: {
          event: movement,
          modified: observation || movement,
          stock: { ...stock, lastChangeUser: await getUser() },
        },
      }, 'post');
    } catch {}
  };

  const handleSave = async () => {
    if (!variant || !groupStock) return;
    setSaving(true);
    try {
      const results = await Promise.all(
        sizes.map((size) => saveSize(variant._id, size, groupStock[size] ?? 0))
      );
      await Promise.all(results.map((r) => persistLog(r?.stock ?? {})));
      toast.success('Estoque atualizado com sucesso!');
      setObservation('');
      setLogsPage(1);
      loadLogs(1);
    } catch (err) {
      toast.error(err.message || 'Erro ao salvar estoque.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!variant || !groupStock) return;
    setSaving(true);
    try {
      await Promise.all(sizes.map((size) => saveSize(variant._id, size, 0)));
      toast.success('Estoque zerado com sucesso!');
      setConfirmReset(false);
      loadVariant();
      setLogsPage(1);
      loadLogs(1);
    } catch (err) {
      toast.error(err.message || 'Erro ao zerar estoque.');
    } finally {
      setSaving(false);
    }
  };

  const product = variant?.product;
  const pictures = variant?.pictures ?? [];

  return (
    <div className="sdp-page">
      <ToastContainer />

      {/* ── Header ── */}
      <div className="sdp-header">
        <div className="sdp-header-left">
          <button className="sdp-back" onClick={() => history.push('/admin-panel/Stocks')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
          </button>
          <h2 className="sdp-title">Estoque atual</h2>
        </div>
        <div className="sdp-header-right">
          <button className="sdp-btn-zpl" onClick={() => setShowZpl(true)} disabled={!variant}>
            Gerar ZPL
          </button>
        </div>
      </div>

      {loading ? (
        <Loading />
      ) : !variant ? (
        <div className="sdp-empty">Variante não encontrada.</div>
      ) : (
        <>
          {/* ── Two-column body ── */}
          <div className="sdp-body">
            {/* LEFT — product image + meta */}
            <div className="sdp-left">
              <div className="sdp-product-card">
                <div className="sdp-main-image-wrap">
                  {pictures[activeImage]?.url ? (
                    <img src={pictures[activeImage].url} alt={product?.name} className="sdp-main-image" />
                  ) : (
                    <div className="sdp-no-image">Sem imagem</div>
                  )}
                </div>
                {pictures.length > 1 && (
                  <div className="sdp-thumbnails">
                    {pictures.slice(0, 5).map((pic, i) => (
                      <button
                        key={i}
                        className={`sdp-thumb${activeImage === i ? ' active' : ''}`}
                        onClick={() => setActiveImage(i)}
                      >
                        <img src={pic.url} alt="" />
                      </button>
                    ))}
                  </div>
                )}
                <div className="sdp-product-meta">
                  {product?.name && <h3 className="sdp-product-name">{product.name}</h3>}
                  {product?.code && <p className="sdp-meta-row">Ref: {product.code}</p>}
                  <p className="sdp-meta-active"><span className="sdp-dot-green" /> Ativo</p>
                  {product?.line && <p className="sdp-meta-row">Coleção: {product.line}</p>}
                  {variant?.colorName && <p className="sdp-meta-row">Cor: {variant.colorName}</p>}
                  {product?.category && <p className="sdp-meta-row">Categoria: {product.category}</p>}
                  {product?.shortDetails && <p className="sdp-meta-row">Composição: {product.shortDetails}</p>}
                </div>
              </div>
            </div>

            {/* RIGHT — stock tiles + edit form + additional info */}
            <div className="sdp-right">
              {/* Read-only tiles */}
              <div className="sdp-stock-view-card">
                <h4 className="sdp-section-title">Estoque atual por tamanho</h4>
                <div className="sdp-tiles">
                  {sizes.map((size) => (
                    <div key={size} className="sdp-tile">
                      <span className="sdp-tile-size">{size}</span>
                      <span className="sdp-tile-qty">{groupStock?.[size] ?? 0}</span>
                      <span className="sdp-tile-dot" />
                      <span className="sdp-tile-label">unidades</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Edit form */}
              <div className="sdp-edit-card">
                <h4 className="sdp-section-title">Editar estoque</h4>
                <p className="sdp-edit-subtitle">Atualize a quantidade de estoque por tamanho.</p>

                <div className="sdp-size-inputs">
                  {sizes.map((size) => (
                    <div key={size} className="sdp-size-col">
                      <span className="sdp-size-header">{size}</span>
                      <input
                        type="number"
                        min="0"
                        className="sdp-size-input"
                        value={groupStock?.[size] ?? 0}
                        onChange={(e) => handleChange(size, e.target.value)}
                        id={size}
                      />
                    </div>
                  ))}
                </div>

                <div className="sdp-form-row">
                  <div className="sdp-form-group">
                    <label className="sdp-form-label">Movimentação</label>
                    <select className="sdp-select" value={movement} onChange={(e) => setMovement(e.target.value)}>
                      {MOVEMENT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sdp-form-group sdp-form-group--wide">
                    <label className="sdp-form-label">Observação (opcional)</label>
                    <input
                      type="text"
                      className="sdp-obs-input"
                      placeholder="Ex: Ajuste de inventário mensal"
                      value={observation}
                      onChange={(e) => setObservation(e.target.value)}
                    />
                  </div>
                </div>

                <div className="sdp-form-actions">
                  {confirmReset ? (
                    <div className="sdp-confirm-reset">
                      <span>Tem certeza?</span>
                      <button className="sdp-btn-confirm-yes" onClick={handleReset} disabled={saving}>Sim, zerar</button>
                      <button className="sdp-btn-link" onClick={() => setConfirmReset(false)}>Cancelar</button>
                    </div>
                  ) : (
                    <button className="sdp-btn-outline-danger" onClick={() => setConfirmReset(true)} disabled={saving}>
                      Zerar estoque
                    </button>
                  )}
                  <button className="sdp-btn-save" onClick={handleSave} disabled={saving}>
                    {saving ? 'Salvando...' : 'Salvar alterações'}
                  </button>
                </div>
              </div>

              {/* Additional info — below the edit form */}
              <div className="sdp-additional-card">
                <h4 className="sdp-additional-title">Informações adicionais</h4>
                <div className="sdp-additional-grid">
                  {product?.price != null && (
                    <div className="sdp-info-block">
                      <span className="sdp-info-label">Preço de venda</span>
                      <span className="sdp-info-value">R$ {fmt(product.price)}</span>
                    </div>
                  )}
                  {product?.salePrice != null && (
                    <div className="sdp-info-block">
                      <span className="sdp-info-label">Preço promocional</span>
                      <span className="sdp-info-value">R$ {fmt(product.salePrice)}</span>
                    </div>
                  )}
                  {costPrice != null && (
                    <div className="sdp-info-block">
                      <span className="sdp-info-label">Preço de custo</span>
                      <span className="sdp-info-value">R$ {fmt(costPrice)}</span>
                    </div>
                  )}
                  <div className="sdp-info-block">
                    <span className="sdp-info-label">Estoque total</span>
                    <span className="sdp-info-value">{groupStock?.TOTAL ?? 0} unidades</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── History table ── */}
          <div className="sdp-history-card">
            <div className="sdp-history-header">
              <h4 className="sdp-section-title">Histórico de movimentações</h4>
              {logsTotal > 0 && (
                <span className="sdp-history-count">{logsTotal} registro{logsTotal !== 1 ? 's' : ''}</span>
              )}
            </div>

            {logsLoading ? (
              <Loading size="sm" />
            ) : logs.length === 0 ? (
              <p className="sdp-history-empty">Nenhuma movimentação registrada.</p>
            ) : (
              <>
                <table className="sdp-table">
                  <thead>
                    <tr>
                      <th>DATA</th>
                      <th>TIPO</th>
                      <th>TAMANHO</th>
                      <th>QUANTIDADE</th>
                      <th>USUÁRIO</th>
                      <th>OBSERVAÇÃO</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log._id}>
                        <td>{moment(log.createdAt).format('DD/MM/YYYY HH:mm')}</td>
                        <td>{log.event}</td>
                        <td>{log.stock?.size ?? '—'}</td>
                        <td className={`sdp-qty ${log.stock?.quantity > 0 ? 'sdp-qty--pos' : ''}`}>
                          {log.stock?.quantity != null ? `+${log.stock.quantity}` : '—'}
                        </td>
                        <td>{log.createUser?.name ?? log.createUser?.email ?? '—'}</td>
                        <td>{log.modified}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {totalPages > 1 && (
                  <div className="sdp-pagination">
                    <button
                      className="sdp-page-btn"
                      onClick={() => handlePageChange(logsPage - 1)}
                      disabled={logsPage === 1}
                    >
                      ‹
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                      <button
                        key={p}
                        className={`sdp-page-btn${logsPage === p ? ' active' : ''}`}
                        onClick={() => handlePageChange(p)}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      className="sdp-page-btn"
                      onClick={() => handlePageChange(logsPage + 1)}
                      disabled={logsPage === totalPages}
                    >
                      ›
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {showZpl && variant && (
        <ZplModal variant={variant} groupStock={groupStock} onClose={() => setShowZpl(false)} />
      )}

      {saving && <Loading overlay />}
    </div>
  );
};

export default StockDetailPage;
